import {
  IconAlertCircle,
  IconEdit,
  IconEditCircle,
  IconLogin,
  IconReport,
  IconSettings,
  IconTrash,
} from "@tabler/icons-react";
import { useLayoutEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ContactUser from "../components/chat/core/ContactUser.tsx";
import {
  default as Authorized,
  default as IfAuthorized,
} from "../components/core/auth/Authorized";
import ErrMessage from "../components/core/ErrMessage.tsx";
import { getFileURLClosestTo } from "../components/core/image/Image.tsx";
import NavigateBackButton from "../components/core/navigate/NavigateBackButton.tsx";
import PopUp, { openModal } from "../components/core/PopUp";
import PopUpDialog from "../components/core/PopUpDialog";
import SEO from "../components/core/SEO.tsx";
import Footer from "../components/Footer.tsx";
import EditItem from "../components/item/auth/EditItem";
import IfItemOwner from "../components/item/coditions/IfItemOwner.tsx";
import SetStatus from "../components/item/core/SetStatus";
import ItemView from "../components/item/view/ItemView.tsx";
import LoginForm from "../components/login/LoginForm.tsx";
import { MODAL_LOGIN_ID } from "../components/login/LoginModal.tsx";
import ReportPost from "../components/report/ReportPost";
import { ROUTE_ADMIN_ITEM, ROUTE_PRODUCT } from "../const/routes";
import { EMPTY_ITEM } from "../const/typeDefault/Item";
import { SubmitProvider } from "../context/context.submit";
import { useItem } from "../fetch/fetch.item";
import { UserRole } from "../types/User.ts";

const ItemSEO = ({ itemId }: { itemId: string }) => {
  const { item, isLoading, isError } = useItem(itemId, EMPTY_ITEM);
  if (isLoading || isError) return null;
  return (
    <SEO
      title={item.title}
      description={`${item.text} für ${item.price}€ auf Surfanzeigen.`}
      image={item.imageUrls[0] && getFileURLClosestTo(item.imageUrls[0], 1200)}
      url={ROUTE_PRODUCT(itemId)}
    />
  );
};

function ItemPage() {
  useLayoutEffect(() => scrollTo({ top: 0, behavior: "instant" }), []);
  const { id } = useParams<{ id: string }>();
  const { isError } = useItem(id || "missing", EMPTY_ITEM);

  const navigate = useNavigate();
  if (!id)
    return (
      <ErrMessage
        message={
          "Unter der eingegebenen Adresse konnten wir keine Anzeige finden."
        }
      />
    );
  if (isError) return <ErrMessage message={isError} />;

  return (
    <div className="safe-bottom-with-dock">
      <ItemSEO itemId={id} />
      <div className="relative flex flex-col gap-5 items-center bg-[url(/background.png)] bg-position-[50%_20%] lg:bg-position-[60%_50%] bg-cover">
        <div className="absolute inset-0 bg-linear-to-b from-info/60 via-accent/20 to-base-300" />
        <PopUp
          id="report-item"
          title={
            <>
              <IconReport />
              Beschreibe das Problem
            </>
          }
        >
          <SubmitProvider iButtonElem="Upload">
            <ReportPost itemId={id} />
          </SubmitProvider>
        </PopUp>
        <PopUp
          id={MODAL_LOGIN_ID}
          title={
            <>
              <IconLogin />
              Anmelden
            </>
          }
        >
          <LoginForm />
        </PopUp>
        <SubmitProvider>
          <SetStatus
            itemId={id}
            onSuccess={() => navigate(ROUTE_PRODUCT(id), { replace: true })}
          >
            <EditItem
              fallback={
                <button
                  type="button"
                  className="btn btn-primary btn-disabled animate-pulse opacity-75"
                >
                  <IconEditCircle stroke={2} />
                </button>
              }
              itemId={id}
            >
              <div className="btn w-full">
                <IconEdit />
                Bearbeiten
              </div>
            </EditItem>
          </SetStatus>
        </SubmitProvider>
        <div className="safe-hanger w-full">
          <div className="flex flex-col items-center gap-2 justify-start w-full h-full min-h-screen">
            <nav className="sticky md:relative safe-top flex w-full justify-between gap-8 items-center top-0 glass-blurry bg-base-100/40 lg:opacity-100 md:rounded-box xl:w-1/2 md:w-2/3 touch-none z-9">
              <div className="p-2 ">
                <NavigateBackButton />
              </div>

              <div className="flex p-2 gap-2">
                <IfAuthorized
                  ifUnauthorized={
                    <button
                      type="button"
                      className="btn rounded-full text-info"
                      onClick={() => openModal(MODAL_LOGIN_ID)}
                    >
                      <IconLogin className="opacity-75" stroke={2} />
                      zum login
                    </button>
                  }
                >
                  <IfItemOwner itemId={id} fallback={<ContactUser id={id} />}>
                    <SubmitProvider>
                      <button
                        type="button"
                        className="btn rounded-full active:btn-info"
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal(`item-setting`);
                        }}
                      >
                        <IconSettings />
                        Anzeige
                      </button>
                    </SubmitProvider>
                  </IfItemOwner>
                </IfAuthorized>
              </div>
            </nav>
            <div className="w-full xl:w-1/2 md:w-2/3 justify-center rounded-box glass-blurry p-1">
              <ItemView itemId={id} />
              <div className="divider">Anzeige melden</div>
              <div className="rounded-box w-full p-5">
                <Authorized
                  ifUnauthorized={
                    <Link
                      className="btn btn-neutral w-full"
                      to="mailto:kontakt@surfanzeigen.de"
                    >
                      Anzeige an kontakt@surfanzeigen.de melden
                    </Link>
                  }
                >
                  <PopUpDialog
                    title={
                      <>
                        <IconReport />
                        Melden?
                      </>
                    }
                    id={"report_item"}
                    className="btn"
                    onClick={() => openModal("report-item")}
                    description="Bist du sicher, dass du diese Anzeigen Melden möchtest?"
                  >
                    <IconAlertCircle />
                    melden
                  </PopUpDialog>
                </Authorized>

                <Authorized role={UserRole.Moderator}>
                  <Link className="btn btn-error" to={ROUTE_ADMIN_ITEM(id)}>
                    <IconTrash />
                    löschen
                  </Link>
                </Authorized>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default ItemPage;
